import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Autocomplete,
  Button,
  Checkbox,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { PreLoader } from "../../subcomponents/Preloader";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  fillAutocompleteValues,
  updateData,
} from "../../actions/actionItems";
import InfoIcon from "@mui/icons-material/Info";
import DraggableItemList from "../../subcomponents/DNDComponent";
import AlertComponent from "../../subcomponents/AlertComponent";
import { guestTypeTooltip } from "../../../constants/TooltipInformation";
import { RoomPropertyEnum } from "../../../constants/NavbarList";
import { routeList } from "../../../constants/GuestType";
import {
  validateIntegerLength,
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import useScrollToTop from "../../hooks/useScrollToTop";

const GuestType = ({ data, fieldValues }: { data: any; fieldValues: any }) => {
  const [guestCode, setGuestCode] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<boolean>(false);
  const [guestName, setGuestName] = useState<string | null>(null);
  const [nameError, setNameError] = useState<boolean>(false);
  const [routeLocalCalls, setRouteLocalCalls] = useState<string | null>(null);
  const [turndownService, setTurndownService] = useState<boolean>(false);
  const [skipResortFee, setSkipResortFee] = useState<boolean>(false);
  const [preAuthAmount, setPreAuthAmount] = useState<number | null>(null);
  const [amountError, setAmountError] = useState<boolean>(false);
  const [taxExemptCategory, setTaxExemptCategory] = useState<string | null>(
    null,
  );
  const [guestTypeInformation, setGuestTypeInformation] = useState<any>(null);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [taxExemptList, setTaxExemptList] = useState<string[]>([]);
  const [active, setActive] = useState<boolean>(true);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const { topRef, scrollToTop } = useScrollToTop();

  const guestTypePayload = {
    code: guestCode,
    name: guestName,
    isRouteLocalCalls: routeLocalCalls ?? false,
    isTurndown: turndownService ?? false,
    isSkipResortFee: skipResortFee ?? false,
    creditCardPreAuth: preAuthAmount ?? 0,
    taxExemptCategories: taxExemptCategory ?? "",
    isActive: active ?? true,
  };

  const addGuestType = () => {
    if (validateInput()) {
      if (!guestCode || guestCode === null || guestCode === "") {
        setCodeError(true);
      }
      if (!guestName || guestName === null || guestName === "") {
        setNameError(true);
      } else if (
        checkDuplicate(
          Array.isArray(guestTypeInformation)
            ? guestTypeInformation
            : guestTypeInformation
              ? [guestTypeInformation]
              : [],
          DNDFieldsEnum.GUESTTYPECODE,
          guestCode,
        )
      ) {
        setAlertMessage("Cannot have duplicate entries");
        setAlertType("error");
        setOpenAlert(true);
      } else {
        setGuestTypeInformation((prev: any) => {
          return updateData(
            Array.isArray(prev)
              ? [
                  ...prev,
                  {
                    id: prev.length + 1,
                    ...guestTypePayload,
                  },
                ]
              : [
                  {
                    id: 1,
                    ...guestTypePayload,
                  },
                ],
          );
        });
        setLastUpdatedRecord(guestTypePayload);
        editGuestType(null);
        setEditClicked(false);
      }
    }
  };

  const editGuestType = (list: any) => {
    list?.id && setSelectedId(list.id);
    list !== null && setEditClicked(true);
    setGuestCode(list?.code);
    setGuestName(list?.name);
    setRouteLocalCalls(list?.isRouteLocalCalls);
    setTurndownService(list ? list.isTurndown : false);
    setSkipResortFee(list ? list.isSkipResortFee : false);
    setPreAuthAmount(list?.creditCardPreAuth);
    setTaxExemptCategory(list?.taxExemptCategories);
    setActive(list ? list.isActive : true);
  };

  const updateGuestType = () => {
    if (!validateInput()) return false;
    if (
      checkDuplicate(
        (Array.isArray(guestTypeInformation)
          ? guestTypeInformation
          : guestTypeInformation
            ? [guestTypeInformation]
            : []
        ).filter((item: any) => String(item.id) !== String(selectedId)),
        DNDFieldsEnum.GUESTTYPECODE,
        guestCode,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    setNameError(false);
    setGuestTypeInformation((prev: any) => {
      const items = Array.isArray(prev) ? prev : prev != null ? [prev] : [];
      return updateData(
        items.map((item: any) =>
          String(item.id) === String(selectedId)
            ? { ...item, ...guestTypePayload }
            : item,
        ),
      );
    });
    editGuestType(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${RoomPropertyEnum.guestType} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const deleteGuestType = (id: number) => {
    setGuestTypeInformation(deleteItem(guestTypeInformation, id));
  };

  const validateInput = () => {
    setCodeError(false);
    setNameError(false);
    setAmountError(false);
    let isValid = true;
    if (
      guestCode &&
      !validateString(
        guestCode,
        validationLength.guestTypeCode,
        validationType.CODE,
      )
    ) {
      setCodeError(true);
      isValid = false;
    }
    if (
      guestName &&
      !validateString(
        guestName,
        validationLength.guestTypeName,
        validationType.OTHER,
      )
    ) {
      setNameError(true);
      isValid = false;
    }
    if (
      preAuthAmount &&
      !validateIntegerLength(preAuthAmount, validationLength.preAuthAmount)
    ) {
      setAmountError(true);
      isValid = false;
    }
    return isValid;
  };

  useEffect(() => {
    data(guestTypeInformation);
    editClicked &&
      setSelectedId(
        checkData(guestTypeInformation, selectedId) ? selectedId : null,
      );
  }, [guestTypeInformation]);

  useEffect(() => {
    validateInput();
  }, [guestCode, guestName, preAuthAmount]);

  useEffect(() => {
    if (fieldValues) {
      setGuestTypeInformation(
        Array.isArray(fieldValues[RoomPropertyEnum.guestType])
          ? fieldValues[RoomPropertyEnum.guestType]
          : fieldValues[RoomPropertyEnum.guestType]
            ? [fieldValues[RoomPropertyEnum.guestType]]
            : null,
      );
      setTaxExemptList(
        fillAutocompleteValues(
          fieldValues[RoomPropertyEnum.taxExemptCategory],
          DNDFieldsEnum.TAXEXEMPTCODE,
          DNDFieldsEnum.TAXEXEMPTNAME,
        ),
      );
    }
  }, []);

  return (
    <Stack direction="column" gap="10px">
      <div ref={topRef} />
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            width="100%"
            alignItems="center"
            gap="10px"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              Guest Type Information
              <Tooltip title={tooltipComponent(guestTypeTooltip.header)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton
                  click={() => {
                    editGuestType(null);
                    setSelectedId(null);
                  }}
                />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editGuestType(lastUpdatedRecord);
                  }}
                />
              )}
            </Stack>
          </Stack>
          <Stack
            className="form-two-col-row"
            direction="row"
            gap="20px"
            width="100%"
          >
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Guest Type Code
                <Tooltip
                  title={tooltipComponent(guestTypeTooltip.guestTypeCode)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="GuestTypeCode"
                variant="outlined"
                placeholder="Guest Type Code"
                value={guestCode ?? ""}
                error={codeError}
                required
                sx={{ width: "100%" }}
                helperText={
                  codeError
                    ? validationMessages.guestTypeCode
                    : guestTypeTooltip.guestTypeCode
                }
                onChange={(e: any) => {
                  setGuestCode(e.target.value.toUpperCase());
                }}
              />
            </Stack>
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Guest Type Name
                <Tooltip
                  title={tooltipComponent(guestTypeTooltip.guestTypeName)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="GuestTypeName"
                variant="outlined"
                placeholder="Guest Type Name"
                value={guestName ?? ""}
                error={nameError}
                required
                sx={{ width: "100%" }}
                helperText={
                  nameError
                    ? validationMessages.guestTypeName
                    : guestTypeTooltip.guestTypeName
                }
                onChange={(e: any) => {
                  setGuestName(e.target.value);
                }}
              />
            </Stack>
          </Stack>
          <Stack
            className="form-two-col-row"
            direction="row"
            gap="20px"
            width="100%"
          >
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Route Local Calls
                <Tooltip
                  title={tooltipComponent(guestTypeTooltip.routeLocalCalls)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <Select
                id="routelocalcalls"
                value={routeLocalCalls ?? ""}
                onChange={(e: any) => setRouteLocalCalls(e.target.value)}
                sx={{ width: "100%" }}
              >
                {routeList.map((route, index) => (
                  <MenuItem
                    value={route.toLowerCase().split(" ").join("_")}
                    key={index}
                  >
                    {route}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Credit Card Pre Auth Amount
                <Tooltip
                  title={tooltipComponent(
                    guestTypeTooltip.creditCardPreAuthAmount,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="creditCardPreAuthAmount"
                variant="outlined"
                placeholder="Credit Card Pre Auth Amount"
                type="number"
                error={amountError}
                value={preAuthAmount ?? ""}
                sx={{ width: "100%" }}
                helperText={
                  amountError
                    ? validationMessages.preAuthAmount
                    : guestTypeTooltip.creditCardPreAuthAmount
                }
                onChange={(e: any) => {
                  setPreAuthAmount(e.target.value >= 0 ? e.target.value : null);
                }}
              />
            </Stack>
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Tax Exempt Categories
                <Tooltip
                  title={tooltipComponent(guestTypeTooltip.taxExemptCategories)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <Autocomplete
                id="taxExemptCategories"
                disablePortal
                options={taxExemptList}
                sx={{ width: "100%" }}
                disabled={!taxExemptList}
                value={taxExemptCategory ?? ""}
                onChange={(e: any) => setTaxExemptCategory(e.target.innerText)}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Tax Exempt categories" />
                )}
              />
            </Stack>
          </Stack>
          <Stack
            className="form-checkbox-row form-two-col-row"
            direction="row"
            width="100%"
            gap="20px"
            flexWrap="wrap"
          >
            {/* <Stack direction="row" width="max-content" height="max-content">
                            <FormControlLabel control={
                                <Checkbox
                                    checked={routeLocalCalls}
                                    onChange={(e: any) => setRouteLocalCalls(e.target.checked)}
                                />}
                                label="Route local calls ?"
                            />
                            <Tooltip title={tooltipComponent(guestTypeTooltip.routeLocalCalls)}>
                                <InfoIcon fontSize="small" />
                            </Tooltip>
                        </Stack> */}
            <Stack direction="row" width="max-content" height="max-content">
              <FormControlLabel
                control={
                  <Checkbox
                    id="turndownService"
                    checked={turndownService}
                    onChange={(e: any) => setTurndownService(e.target.checked)}
                  />
                }
                label="Turndown service?"
              />
              <Tooltip
                title={tooltipComponent(guestTypeTooltip.turndownService)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <Stack direction="row" width="max-content" height="max-content">
              <FormControlLabel
                control={
                  <Checkbox
                    id="skipResortFee"
                    checked={skipResortFee}
                    onChange={(e: any) => setSkipResortFee(e.target.checked)}
                  />
                }
                label="Skip resort fee?"
              />
              <Tooltip title={tooltipComponent(guestTypeTooltip.skipResortFee)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
          </Stack>
          <Stack direction="row" gap="2px">
            <FormControlLabel
              control={
                <Switch checked={active} onChange={() => setActive(!active)} />
              }
              label="Active?"
            />
            {/* <Tooltip title={tooltipComponent("")}>
              <InfoIcon fontSize="small" />
            </Tooltip> */}
          </Stack>
          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={selectedId ? updateGuestType : addGuestType}
              id="addNewGuestType"
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Guest Type
            </Button>
          </Stack>
          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="h6">Guest Type Available</Typography>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Stack direction="column" gap="10px">
              {guestTypeInformation ? (
                <DraggableItemList
                  itemData={guestTypeInformation}
                  onOrderUpdate={(order) => setGuestTypeInformation(order)}
                  onEdit={(info) => {
                    editGuestType(info);
                    scrollToTop();
                  }}
                  onDelete={(id) => deleteGuestType(id)}
                  code={DNDFieldsEnum.GUESTTYPECODE}
                  name={DNDFieldsEnum.GUESTTYPENAME}
                />
              ) : (
                <Typography variant="body2">
                  No Guest Type Information Available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default GuestType;
